import { Container, Graphics } from "pixi.js";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  color: number;
  size: number;
  gravity: number;
}

export class Confetti {
  private container: Container;
  private particles: Particle[] = [];
  private graphics: Graphics;
  private isAnimating: boolean = false;

  constructor() {
    this.container = new Container();
    this.graphics = new Graphics();
    this.container.addChild(this.graphics);
  }

  public getContainer(): Container {
    return this.container;
  }

  public burst(x: number, y: number, count: number = 50): void {
    const colors = [
      0xff6b6b, // Red
      0x4ecdc4, // Teal
      0xffe66d, // Yellow
      0x95e1d3, // Mint
      0xf38181, // Pink
      0xaa96da, // Purple
      0xfcbad3, // Light Pink
      0xa8e6cf, // Light Green
    ];

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
      const speed = 3 + Math.random() * 5;

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 3, // Initial upward bias
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 10 + Math.random() * 12,
        gravity: 0.15 + Math.random() * 0.1,
      });
    }

    if (!this.isAnimating) {
      this.isAnimating = true;
      this.animate();
    }
  }

  public burstDirectional(
    x: number,
    y: number,
    count: number,
    angleOffset: number,
    upwardBias: number,
  ): void {
    const colors = [
      0xff6b6b, // Red
      0x4ecdc4, // Teal
      0xffe66d, // Yellow
      0x95e1d3, // Mint
      0xf38181, // Pink
      0xaa96da, // Purple
      0xfcbad3, // Light Pink
      0xa8e6cf, // Light Green
    ];

    for (let i = 0; i < count; i++) {
      // Much wider spread to cover entire screen
      const spread = (Math.random() - 0.5) * Math.PI * 1.8; // Very wide spread
      const angle = -Math.PI / 2 + angleOffset + spread; // Start pointing up
      const speed = 18 + Math.random() * 16; // Faster initial speed

      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed + upwardBias,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 8 + Math.random() * 8,
        gravity: 0.5 + Math.random() * 0.3, // Higher gravity for faster fall
      });
    }

    if (!this.isAnimating) {
      this.isAnimating = true;
      this.animate();
    }
  }

  private animate(): void {
    if (this.particles.length === 0) {
      this.isAnimating = false;
      return;
    }

    this.graphics.clear();

    // Update and draw particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      // Update physics
      p.vy += p.gravity;
      p.x += p.vx;
      p.y += p.vy;
      p.rotation += p.rotationSpeed;

      // Remove if off screen (below) AND moving downward
      if (p.y > window.innerHeight + 100 && p.vy > 0) {
        this.particles.splice(i, 1);
        continue;
      }

      // Draw rotated rectangle
      const cos = Math.cos(p.rotation);
      const sin = Math.sin(p.rotation);
      const halfSize = p.size / 2;

      // Calculate corners of rotated rectangle
      const x1 = p.x + (-halfSize * cos - -halfSize * sin);
      const y1 = p.y + (-halfSize * sin + -halfSize * cos);
      const x2 = p.x + (halfSize * cos - -halfSize * sin);
      const y2 = p.y + (halfSize * sin + -halfSize * cos);
      const x3 = p.x + (halfSize * cos - halfSize * sin);
      const y3 = p.y + (halfSize * sin + halfSize * cos);
      const x4 = p.x + (-halfSize * cos - halfSize * sin);
      const y4 = p.y + (-halfSize * sin + halfSize * cos);

      this.graphics.poly([x1, y1, x2, y2, x3, y3, x4, y4]);
      this.graphics.fill({ color: p.color });
    }

    requestAnimationFrame(() => this.animate());
  }

  public clear(): void {
    this.particles = [];
    this.graphics.clear();
    this.isAnimating = false;
  }
}
